import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import SectionsHead from '../../../../../../../common/components/SectionsHead/SectionsHead';
import TopLegend from '../../../../../../../common/components/TopLegend/TopLegend';
import EvolutionLegend from '../../../components/EvolutionLegend/EvolutionLegend';
import * as SC_PA from '../../../PreferanceAnalysis.styles';
import CombinedBarLineChart from '../../../../../../../common/components/CombinedBarLineChart/CombinedBarLineChart';
import RankingTable from '../../../components/RankingTable/RankingTable';
import * as SC from './Image.styles';
import { useCombinedLOBS } from '../../hooks/useCombinedLOBS';
import { DataTable } from '@toluna-ui-toolkit/data-table';
import { defaultTheme } from '../../../../../../../utils/defaultTheme';
import * as TabLayout from '../../Tabs.styles';
import {
    CUSTOM_LINECHART_WIDTH_PAGE_FLAG,
    SECTION_DATA_TYPE,
    brandsObjectProcessing,
    checkedBrandsEvolutionExportFormat,
    resetBrandsListLOBLegendObject,
    sectionsHeadColor,
} from '../../utils/utils';
import NoDataAvailable from '../../../../../../../common/components/NoDataAvailable/NoDataAvailable';
import Legend from '../../../../../../../common/components/Legend/Legend';
import { checkedEvolutionBrands } from '../../../data/actions';
import { SCOPE_TYPES } from '../../../../../../../common/utils/utils';
import ToggleSectionContainer from '../../../components/ToggleContainer/ToggleSection.container';
import LOBLegend from '../../../components/LOBLegend/LOBLegend';
import { customStyleLOBLegend } from '../../Tabs.styles';
import EvolutionCombinedChartSection from '../../../components/EvolutionCombinedChartSection/EvolutionCombinedChartSection';
import { TRANSLATION_TEXT } from '../../../../../../../utils/translations';

const ImageTab = ({
    imageSectionIDs,
    combinedChartsData,
    lobsAsOptions,
    combinedBarLineChartLegend,
    rankingTableData,
    imageData,
    hasTopFilterOptionLoaded,
    lineChartData,
    getBptPreferenceAnalysisImageActionCreator,
    showTableOnSection,
    tableHeaderAndData,
    lineChartDescription,
    filtersThrownError,
    appliedFilters,
    contentScope,
    evolutionBrands,
    isEvolutionEnabled,
    checkedEvolutionBrands: checkedBrands,
}) => {
    const dispatch = useDispatch();
    const { tableHeader, tableData } = tableHeaderAndData;
    const evolutionCompetitionWrapperRef = useRef(null);

    const imageDataLoaded = () =>
        imageData && imageData.hasOwnProperty(SECTION_DATA_TYPE.DEFAULT) && imageData.sections && !filtersThrownError;
    const imageDataForEvolutionLoaded = () =>
        imageData &&
        evolutionBrands &&
        isEvolutionEnabled &&
        imageData.hasOwnProperty(SECTION_DATA_TYPE.EVOLUTION) &&
        imageData.sectionsEvolutionReport &&
        !filtersThrownError;

    //brands handling for evolution scope
    const imageBrandsEvolution = evolutionBrands ? brandsObjectProcessing(evolutionBrands) : [];
    const [evolutionBrandsState, setEvolutionBrandsState] = useState(imageBrandsEvolution);

    useEffect(() => {
        if (evolutionBrands && imageBrandsEvolution.length > 0) {
            setEvolutionBrandsState(
                imageBrandsEvolution.map(brand => {
                    const isCheckedInGlobal = checkedBrands.some(globalBrand => globalBrand.brandId === brand.key);
                    return {
                        ...brand,
                        isChecked: isCheckedInGlobal || brand.isChecked,
                    };
                })
            );
        }
    }, [evolutionBrands]);

    const onBrandsCheckboxChange = useCallback(key => {
        setEvolutionBrandsState(prev =>
            prev.map(brand => (brand.key === key ? { ...brand, isChecked: !brand.isChecked } : brand))
        );
    }, []);

    const [combinedChartLOBs, , getVisibleLOBs] = useCombinedLOBS({
        sectionIDS: imageSectionIDs,
        lobsAsOptions,
    });
    useEffect(() => {
        if (hasTopFilterOptionLoaded) getBptPreferenceAnalysisImageActionCreator();
    }, [hasTopFilterOptionLoaded]);

    const getLineChartLOBS = (sectionID, index) => {
        return lineChartData[sectionID][index]?.lineData.map(line => line.name);
    };

    const renderLegendForTableIndex = 0;

    const tableHeaderMemoized = useMemo(() => {
        if (tableHeader && tableHeader.length) {
            return [...tableHeader];
        }
        return [];
    }, [tableData]);

    const TABLE_STYLE = {
        global: {
            fontFamily: 'Source Sans Pro,Open Sans',
        },
        tableHead: {
            backgroundColor: 'null',
            borderBottom: `2px solid ${defaultTheme.blue[750]}`,
            borderTop: 'none',
            borderLeft: 'none',
            borderRight: 'none',
            fontSize: '8px',
            color: defaultTheme.blue[750],
            borderTopRightRadius: '0',
            borderTopLeftRadius: '0',
        },
        bodyRow: {
            activeColor: null,
            backgroundColor: defaultTheme.white.ff,
            backgroundColorSecondary: defaultTheme.grey[50],
            hoverColor: defaultTheme.blue[50],
            borderTop: 'none',
        },
        column: { separatorColor: 'none' },
    };

    const hasSideFiltersApplied = appliedFilters && appliedFilters?.length > 0;
    const isEvolutionScope = contentScope === SCOPE_TYPES.BPT_EVOLUTION && isEvolutionEnabled;

    useEffect(() => {
        if (evolutionBrandsState.length > 0) {
            dispatch(
                checkedEvolutionBrands(isEvolutionScope ? checkedBrandsEvolutionExportFormat(evolutionBrandsState) : [])
            );
        }
    }, [isEvolutionScope, evolutionBrandsState, setEvolutionBrandsState]);

    const imageSectionsSorted = imageData?.sections?.sort((a, b) => a.sectionOrder - b.sectionOrder);
    const imageEvolutionSectionsSorted = imageData?.sectionsEvolutionReport?.sort(
        (a, b) => a.sectionOrder - b.sectionOrder
    );

    return (
        <>
            {!filtersThrownError && <TopLegend rankingLegendIsVisible={!isEvolutionScope} />}
            {!filtersThrownError && hasSideFiltersApplied && <EvolutionLegend />}
            {!filtersThrownError && isEvolutionEnabled && <ToggleSectionContainer />}
            {isEvolutionScope && evolutionBrands && (
                <div style={{ padding: '0 65px 25px' }}>
                    <LOBLegend
                        LOBs={evolutionBrandsState.filter(item => item.key !== 0)}
                        sectionID={1}
                        onChangeHandler={e => onBrandsCheckboxChange(e)}
                        styleObject={{
                            _optionGroupWrapper: {
                                ...customStyleLOBLegend,
                                justifyContent: 'flex-start',
                                alignContent: 'space-evenly',
                                alignItems: 'baseline',
                                minHeight: '55px',
                                padding: '10px 50px',
                            },
                            _legendWrapper: { width: '100%' },
                            _optionWrapper: { minWidth: '140px' },
                        }}
                        globalSelectionProps={null}
                        resetSelectionsProps={{
                            ...resetBrandsListLOBLegendObject(evolutionBrandsState, setEvolutionBrandsState),
                        }}
                        hasGlobalSelectionSection={false}
                        isEvolutionReport={true}
                    />
                </div>
            )}

            {!isEvolutionScope ? (
                <>
                    {imageDataLoaded() ? (
                        <>
                            {imageSectionsSorted.map(({ sectionID, sectionLabel }) => (
                                <SectionsHead
                                    key={sectionID}
                                    title={sectionLabel}
                                    isResponsive={false}
                                    customStyle={sectionsHeadColor}
                                >
                                    {combinedChartsData && combinedChartLOBs && (
                                        <TabLayout.SectionRow
                                            style={{ margin: sectionID === 1 ? '70px 0px 0px 0px' : '0px' }}
                                        >
                                            <TabLayout.SectionItem>
                                                <SC.CombinedBarLineChartWrapper>
                                                    <SC_PA.CombinedChartsWrapper>
                                                        {combinedChartsData[sectionID] &&
                                                            combinedChartsData[sectionID].map((d, index) => (
                                                                <SC.CombinedBarLineChartWrapper
                                                                    key={sectionID + index}
                                                                    data-testid={`Image-Tab-CombinedBarLineChart-${sectionID}`}
                                                                >
                                                                    <CombinedBarLineChart
                                                                        visibleLOBs={getVisibleLOBs(sectionID)}
                                                                        lineData={d.lineData}
                                                                        barData={d.barData}
                                                                        numberOfRespondents={
                                                                            d?.numberOfRespondents || []
                                                                        }
                                                                        customLineTooltip={{
                                                                            tooltipFontColor: defaultTheme.blue[900],
                                                                        }}
                                                                    />
                                                                    <>
                                                                        {combinedBarLineChartLegend &&
                                                                            index === renderLegendForTableIndex && (
                                                                                <Legend
                                                                                    customStyle={{
                                                                                        _wrapper: {
                                                                                            marginLeft: '20px',
                                                                                        },
                                                                                        _individualItem: {
                                                                                            marginRight: '20px',
                                                                                        },
                                                                                    }}
                                                                                    displayDirection={'row'}
                                                                                    legendItems={
                                                                                        combinedBarLineChartLegend[
                                                                                            sectionID
                                                                                        ]
                                                                                    }
                                                                                />
                                                                            )}
                                                                    </>
                                                                </SC.CombinedBarLineChartWrapper>
                                                            ))}
                                                    </SC_PA.CombinedChartsWrapper>
                                                </SC.CombinedBarLineChartWrapper>
                                            </TabLayout.SectionItem>

                                            {rankingTableData[sectionID] && rankingTableData[sectionID].length > 0 && (
                                                <TabLayout.SectionItem data-testid={'Image-Tab-RankingTable'}>
                                                    <RankingTable
                                                        rankingData={[rankingTableData[sectionID][0]]}
                                                        lobOptions={[]}
                                                        styleObject={{ margin: '-45px 0px 40px 20px' }}
                                                        hasNumberOfRespondents={true}
                                                        hasSideFiltersApplied={hasSideFiltersApplied}
                                                    />
                                                </TabLayout.SectionItem>
                                            )}
                                        </TabLayout.SectionRow>
                                    )}
                                    {lineChartData && (
                                        <TabLayout.SectionRow>
                                            <TabLayout.SectionItem style={{ justifyContent: 'flex-start' }}>
                                                <SC_PA.CombinedChartsWrapper
                                                    style={{ justifyContent: 'flex-start', flexWrap: 'wrap' }}
                                                >
                                                    {lineChartData[sectionID]?.map((d, index) => (
                                                        <SC.LineChartWrapper key={sectionID + index}>
                                                            {sectionID === 4 && (
                                                                <SC.LineChartDescription>
                                                                    {lineChartDescription[sectionID][index]}
                                                                </SC.LineChartDescription>
                                                            )}
                                                            <SC.InnerLineChartWrapper
                                                                data-testid={`Image-Tab-LineChart-${sectionID}`}
                                                            >
                                                                <CombinedBarLineChart
                                                                    visibleLOBs={getLineChartLOBS(sectionID, index)}
                                                                    lineData={d.lineData}
                                                                    barData={[]}
                                                                    numberOfRespondents={
                                                                        hasSideFiltersApplied
                                                                            ? d?.numberOfRespondents
                                                                            : []
                                                                    }
                                                                    customWidthOnPage={
                                                                        CUSTOM_LINECHART_WIDTH_PAGE_FLAG.IMAGE
                                                                    }
                                                                />

                                                                {combinedBarLineChartLegend && index % 2 === 0 && (
                                                                    <Legend
                                                                        customStyle={{
                                                                            _wrapper: { margin: '20px 0 20px 20px' },
                                                                            _individualItem: { marginRight: '20px' },
                                                                        }}
                                                                        displayDirection={'row'}
                                                                        legendItems={
                                                                            combinedBarLineChartLegend[sectionID]
                                                                        }
                                                                    />
                                                                )}
                                                            </SC.InnerLineChartWrapper>
                                                        </SC.LineChartWrapper>
                                                    ))}
                                                </SC_PA.CombinedChartsWrapper>
                                            </TabLayout.SectionItem>
                                        </TabLayout.SectionRow>
                                    )}
                                    {showTableOnSection && showTableOnSection === sectionID && (
                                        <TabLayout.SectionRow>
                                            <TabLayout.SectionItem>
                                                <SC.TableWrapper>
                                                    <DataTable
                                                        data={tableData}
                                                        columns={tableHeaderMemoized}
                                                        isScrollable={false}
                                                        isPaginated={false}
                                                        styles={TABLE_STYLE}
                                                    />
                                                </SC.TableWrapper>
                                            </TabLayout.SectionItem>
                                        </TabLayout.SectionRow>
                                    )}
                                </SectionsHead>
                            ))}
                        </>
                    ) : (
                        <NoDataAvailable show={!imageDataLoaded() && !filtersThrownError} />
                    )}
                </>
            ) : (
                <>
                    {imageDataForEvolutionLoaded() ? (
                        <>
                            {imageEvolutionSectionsSorted.map(({ sectionID, sectionLabel }) => (
                                <SectionsHead
                                    key={sectionID}
                                    title={sectionLabel}
                                    isResponsive={false}
                                    customStyle={sectionsHeadColor}
                                >
                                    <TabLayout.SectionRow ref={evolutionCompetitionWrapperRef}>
                                        <TabLayout.SectionItem>
                                            {combinedChartsData ? (
                                                <EvolutionCombinedChartSection
                                                    sectionID={sectionID}
                                                    legendItems={evolutionBrandsState}
                                                    combinedChartsData={combinedChartsData}
                                                    wrapperRef={evolutionCompetitionWrapperRef}
                                                />
                                            ) : (
                                                <SC_PA.NoDataText>
                                                    {TRANSLATION_TEXT.NO_DATA_AVAILABLE_TEXT}
                                                </SC_PA.NoDataText>
                                            )}
                                        </TabLayout.SectionItem>
                                    </TabLayout.SectionRow>
                                </SectionsHead>
                            ))}
                        </>
                    ) : (
                        <NoDataAvailable show={!imageDataForEvolutionLoaded() && !filtersThrownError} />
                    )}
                </>
            )}
        </>
    );
};

export default ImageTab;
